import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarInset
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Calendar, Clock, Users, User, Lock, LogOut, Activity, Settings, Video, ChevronDown, UserCog, MapPin, Award, FileText, AlertCircle } from "lucide-react";
import { Copy } from "lucide-react";


type TherapistView = 'sessions' | 'clients' | 'schedule' | 'profile-account' | 'profile-professional' | 'profile-location' | 'profile-certifications' | 'profile-pricing' | 'security';

export default function TherapistDashboard() {
  const { user, isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [activeView, setActiveView] = useState<TherapistView>('sessions');
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Session filtering state
  const [sessionFilter, setSessionFilter] = useState<'all' | 'upcoming' | 'completed' | 'cancelled'>('all');
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // Profile form states
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [experience, setExperience] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocationField] = useState("Online");
  const [timezone, setTimezone] = useState("UTC");
  const [education, setEducation] = useState<string[]>([""]);
  const [certifications, setCertifications] = useState<string[]>([""]);
  const [therapyApproaches, setTherapyApproaches] = useState<string[]>([]);
  const [sessionTypes, setSessionTypes] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [insurance, setInsurance] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [therapist, setTherapist] = useState<any>(null);
  const [avatar, setAvatar] = useState(''); // Added avatar state

  // Error states for each section
  const [accountErrors, setAccountErrors] = useState<{firstName?: string, lastName?: string}>({});
  const [professionalErrors, setProfessionalErrors] = useState<{
    approaches?: string, 
    sessionTypes?: string, 
    languages?: string
  }>({});
  const [locationErrors, setLocationErrors] = useState<{bio?: string}>({});
  const [certificationsErrors, setCertificationsErrors] = useState({});
  const [pricingErrors, setPricingErrors] = useState({});

  // Pricing state
  const [pricing, setPricing] = useState({
    individual: { price: 0, duration: 50, currency: 'INR' },
    couples: { price: 0, duration: 60, currency: 'INR' },
    family: { price: 0, duration: 60, currency: 'INR' },
    group: { price: 0, duration: 60, currency: 'INR' }
  });
  const [sessionDurations, setSessionDurations] = useState<number[]>([30, 45, 50, 60]);
  const [acceptsInsurance, setAcceptsInsurance] = useState(false);
  const [slidingScale, setSlidingScale] = useState(false);
  const [freeConsultation, setFreeConsultation] = useState(false);

  // Scheduling state (slot-based)
  type WeeklySchedule = {
    [key: string]: {
      enabled: boolean;
      slots: string[]; // Array of time strings like "9:00 AM"
    };
  };

  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>({
    Monday: { enabled: false, slots: [] },
    Tuesday: { enabled: false, slots: [] },
    Wednesday: { enabled: false, slots: [] },
    Thursday: { enabled: false, slots: [] },
    Friday: { enabled: false, slots: [] },
    Saturday: { enabled: false, slots: [] },
    Sunday: { enabled: false, slots: [] }
  });

  const [blockedDates, setBlockedDates] = useState<Array<{ date: string; reason: string }>>([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");

  // Redirect if not authenticated or not therapist
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'therapist') {
      setLocation('/therapist-login');
    }
  }, [isAuthenticated, user, setLocation]);

  // Load therapist sessions
  useEffect(() => {
    const loadSessions = async () => {
      if (!isAuthenticated || user?.role !== 'therapist' || !user?.id) {
        console.log('⚠️ Skipping session load - auth check failed:', {
          isAuthenticated,
          role: user?.role,
          userId: user?.id
        });
        return;
      }

      console.log('📋 Loading sessions for therapist:', user.id, user.email);
      setSessionsLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/bookings', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch sessions');
        }

        const allBookings = await response.json();
        console.log('📋 Total bookings fetched:', allBookings.length);
        console.log('📋 All bookings:', allBookings);

        // Filter bookings for this therapist only
        const therapistSessions = allBookings.filter((booking: any) => {
          const matches = booking.therapistId === user.id;
          if (matches) {
            console.log('✅ Matching session found:', booking);
          }
          return matches;
        });

        console.log('📋 Therapist sessions after filter:', therapistSessions.length);
        setSessions(therapistSessions);

        if (therapistSessions.length === 0) {
          console.log('⚠️ No sessions found for therapist. Checking all bookings for human sessions...');
          const humanSessions = allBookings.filter((b: any) => b.sessionType === 'human');
          console.log('📋 All human sessions:', humanSessions);
        }
      } catch (error) {
        console.error('❌ Failed to load sessions:', error);
        toast({
          title: "Error",
          description: "Failed to load sessions",
          variant: "destructive"
        });
      } finally {
        setSessionsLoading(false);
      }
    };

    if (activeView === 'sessions') {
      loadSessions();
    }
  }, [isAuthenticated, user, activeView, toast]);

  // Load therapist profile data from /api/therapists/:id
  useEffect(() => {
    const loadProfile = async () => {
      if (!isAuthenticated || user?.role !== 'therapist' || !user?.id) {
        console.log('Skipping profile load - not authenticated or not therapist');
        return;
      }

      try {
        const token = localStorage.getItem('auth_token');
        // Use slug if available, fallback to ID (which will redirect to slug)
        const identifier = (user as any)?.slug || user.id;
        console.log('Loading therapist profile for identifier:', identifier);

        const response = await fetch(`/api/therapists/${identifier}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Raw API response:', data);

        // Check if we got a redirect response (when using ID instead of slug)
        if (data.redirect) {
          // Only re-fetch if it's a permanent slug redirect
          if (data.permanent && data.redirectTo) {
            console.log('Profile loaded via ID, redirected to slug:', data.redirectTo);
            // Re-fetch using slug from redirect
            const slugMatch = data.redirectTo.match(/\/therapist-profile\/([^/]+)$/);
            if (slugMatch) {
              const slug = slugMatch[1];
              console.log('Re-fetching with slug:', slug);

              const slugResponse = await fetch(`/api/therapists/${slug}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });

              if (slugResponse.ok) {
                const slugData = await slugResponse.json();

                // Check if we got another redirect (shouldn't happen with slug)
                if (slugData.redirect) {
                  throw new Error('Unexpected redirect when fetching by slug');
                }

                console.log('Slug-based API response:', slugData);
                setTherapist(slugData);
                populateFormFields(slugData);
              } else {
                throw new Error(`Failed to fetch profile with slug: ${slugResponse.status}`);
              }
            }
            return;
          }

          // For other redirects, throw error
          throw new Error(data.message || 'Therapist profile not accessible');
        }

        // Direct response without redirect
        console.log('Setting therapist data and populating form');
        setTherapist(data);
        populateFormFields(data);

      } catch (error) {
        console.error('Failed to load profile:', error);
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to load profile data",
          variant: "destructive"
        });
      }
    };

    // Helper function to populate form fields
    const populateFormFields = (data: any) => {
      // Basic fields
      setFirstName(data.firstName || ""); // Use firstName from data
      setLastName(data.lastName || "");   // Use lastName from data
      setLicenseNumber(data.licenseNumber || "");
      setSpecialization(data.specialty || "");
      setExperience(data.yearsExperience?.toString() || ""); // Convert number to string for input
      setTitle(data.title || "");
      setBio(data.bio || "");
      setLocationField(data.location || "Online");
      setTimezone(data.timezone || "UTC");
      setAvatar(data.avatar || ''); // Set avatar state

      // JSON fields are already parsed by the endpoint
      setEducation(Array.isArray(data.education) && data.education.length > 0 ? data.education : [""]);
      setCertifications(Array.isArray(data.certifications) && data.certifications.length > 0 ? data.certifications : [""]);
      setTherapyApproaches(Array.isArray(data.approaches) ? data.approaches : []);
      
      // Handle sessionTypes - extract just the type names for UI
      if (Array.isArray(data.sessionTypes) && data.sessionTypes.length > 0) {
        const sessionTypeNames = data.sessionTypes.map((session: any) => {
          // If sessionTypes are already transformed objects, use session.type
          // If they are still strings (e.g., from older data), use the string itself
          return typeof session === 'string' ? session : session.type;
        });
        setSessionTypes(sessionTypeNames);
      } else {
        setSessionTypes([]);
      }

      setLanguages(Array.isArray(data.languages) ? data.languages : []);
      setInsurance(Array.isArray(data.insurance) ? data.insurance : []);

      // Load pricing data if available
      if (data.pricing) {
        try {
          const pricingData = typeof data.pricing === 'string' ? JSON.parse(data.pricing) : data.pricing;
          setPricing({
            individual: pricingData.individual || { price: 0, duration: 50, currency: 'INR' },
            couples: pricingData.couples || { price: 0, duration: 60, currency: 'INR' },
            family: pricingData.family || { price: 0, duration: 60, currency: 'INR' },
            group: pricingData.group || { price: 0, duration: 60, currency: 'INR' }
          });
        } catch (e) {
          console.error('Error parsing pricing data:', e);
        }
      }

      if (data.sessionDurations) {
        try {
          const durations = typeof data.sessionDurations === 'string' ? JSON.parse(data.sessionDurations) : data.sessionDurations;
          setSessionDurations(Array.isArray(durations) ? durations : [30, 45, 50, 60]);
        } catch (e) {
          console.error('Error parsing session durations:', e);
        }
      }

      setAcceptsInsurance(data.acceptsInsurance || false);
      setSlidingScale(data.slidingScale || false);
      setFreeConsultation(data.freeConsultation || false);

      // Load availability data (slot-based) - direct slot loading
      if (data.availability) {
        try {
          const availabilityData = typeof data.availability === 'string' 
            ? JSON.parse(data.availability) 
            : data.availability;

          console.log('Loading availability data (slot-based):', availabilityData);

          const transformedSchedule: WeeklySchedule = {
            Monday: { enabled: false, slots: [] },
            Tuesday: { enabled: false, slots: [] },
            Wednesday: { enabled: false, slots: [] },
            Thursday: { enabled: false, slots: [] },
            Friday: { enabled: false, slots: [] },
            Saturday: { enabled: false, slots: [] },
            Sunday: { enabled: false, slots: [] }
          };

          Object.keys(availabilityData).forEach((dayKey: string) => {
            const dayName = dayKey.charAt(0).toUpperCase() + dayKey.slice(1).toLowerCase() as keyof WeeklySchedule;

            if (transformedSchedule[dayName]) {
              const timeSlots = availabilityData[dayKey];

              if (Array.isArray(timeSlots) && timeSlots.length > 0) {
                // Direct slot loading - database stores individual slots like ["9:00 AM", "10:00 AM"]
                transformedSchedule[dayName] = {
                  enabled: true,
                  slots: timeSlots.filter((slot: string) => typeof slot === 'string' && slot.trim())
                };
              }
            }
          });

          console.log('✅ Transformed schedule (slot-based):', transformedSchedule);
          setWeeklySchedule(transformedSchedule);
        } catch (e) {
          console.error('❌ Error parsing availability data:', e);
        }
      }

      // Ensure slug is available for public profile URL
      if (data.slug) {
        setTherapist(prev => ({ ...prev, slug: data.slug }));
      }
    };


    loadProfile();
  }, [isAuthenticated, user, toast]);

  const handleLogout = async () => {
    await logout();
    setLocation('/therapist-login');
  };

  const handleSaveAccountInfo = async () => {
    // Clear previous errors
    setAccountErrors({});

    // Validate fields
    const errors: any = {};
    if (!firstName?.trim()) {
      errors.firstName = "First name is required";
    }
    if (!lastName?.trim()) {
      errors.lastName = "Last name is required";
    }

    // If there are errors, show them and don't proceed
    if (Object.keys(errors).length > 0) {
      setAccountErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields",
        variant: "destructive"
      });
      // Auto-scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ firstName, lastName, avatar }) // Include avatar in the update
      });

      if (!response.ok) throw new Error('Failed to save account info');

      toast({
        title: "Success",
        description: "Account information updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save account information",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfessional = async () => {
    // Clear previous errors
    setProfessionalErrors({});

    // Validation for mandatory fields
    const errors: any = {};

    if (therapyApproaches.length === 0) {
      errors.approaches = "Please select at least one therapy approach";
    }

    if (sessionTypes.length === 0) {
      errors.sessionTypes = "Please select at least one session type";
    }

    if (languages.length === 0) {
      errors.languages = "Please select at least one language";
    }

    // If there are errors, show them and don't proceed
    if (Object.keys(errors).length > 0) {
      setProfessionalErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields",
        variant: "destructive"
      });
      // Auto-scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/therapists/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          specialization,
          experience: experience ? parseInt(experience) : null,
          title,
          approaches: JSON.stringify(therapyApproaches),
          sessionTypes: JSON.stringify(sessionTypes),
          languages: JSON.stringify(languages)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save professional info');
      }

      toast({
        title: "Success",
        description: "Professional credentials updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save professional credentials",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveLocation = async () => {
    // Clear previous errors
    setLocationErrors({});

    // Validate fields
    const errors: any = {};
    if (bio && bio.length > 500) {
      errors.bio = "Bio must not exceed 500 characters";
    }

    // If there are errors, show them and don't proceed
    if (Object.keys(errors).length > 0) {
      setLocationErrors(errors);
      toast({
        title: "Validation Error",
        description: "Please correct the highlighted fields",
        variant: "destructive"
      });
      // Auto-scroll to first error
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bio,
          location,
          timezone
        })
      });

      if (!response.ok) throw new Error('Failed to save location info');

      toast({
        title: "Success",
        description: "Location and profile information updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save location information",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCertifications = async () => {
    // Clear previous errors
    setCertificationsErrors({});

    // No validation needed for certifications tab (optional fields)
    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');

      // Filter out empty entries
      const cleanEducation = education.filter(e => e.trim());
      const cleanCertifications = certifications.filter(c => c.trim());

      const response = await fetch('/api/therapists/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          education: JSON.stringify(cleanEducation),
          certifications: JSON.stringify(cleanCertifications),
          insurance: JSON.stringify(insurance)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save certifications');
      }

      toast({
        title: "Success",
        description: "Certifications and extended details updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save certifications",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePricing = async () => {
    setPricingErrors({});
    setIsSaving(true);

    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch('/api/therapists/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pricing: JSON.stringify(pricing),
          sessionDurations: JSON.stringify(sessionDurations),
          acceptsInsurance,
          slidingScale,
          freeConsultation
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save pricing');
      }

      toast({
        title: "Success",
        description: "Pricing information updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save pricing",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate session statistics
  const getSessionStats = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    const todaySessions = sessions.filter(s => s.date === today && s.status !== 'cancelled').length;

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    const weekSessions = sessions.filter(s => {
      const sessionDate = new Date(s.date);
      return sessionDate >= startOfWeek && s.status !== 'cancelled';
    }).length;

    const uniqueClients = new Set(sessions.map(s => s.clientUserId)).size;

    return { todaySessions, weekSessions, uniqueClients };
  };

  // Filter sessions based on selected filter
  const getFilteredSessions = () => {
    const now = new Date();
    const nowStr = now.toISOString();

    switch (sessionFilter) {
      case 'upcoming':
        return sessions.filter(s => {
          if (s.status === 'cancelled' || s.status === 'completed') return false;
          const sessionDateTime = new Date(`${s.date}T${s.time}`);
          return sessionDateTime >= now;
        });
      case 'completed':
        return sessions.filter(s => s.status === 'completed');
      case 'cancelled':
        return sessions.filter(s => s.status === 'cancelled' || s.status === 'no-show');
      default:
        return sessions;
    }
  };

  // Get counts for filter cards
  const getFilterCounts = () => {
    const now = new Date();

    return {
      all: sessions.length,
      upcoming: sessions.filter(s => {
        if (s.status === 'cancelled' || s.status === 'completed') return false;
        const sessionDateTime = new Date(`${s.date}T${s.time}`);
        return sessionDateTime >= now;
      }).length,
      completed: sessions.filter(s => s.status === 'completed').length,
      cancelled: sessions.filter(s => s.status === 'cancelled' || s.status === 'no-show').length
    };
  };

  const formatSessionDate = (date: string, time: string) => {
    const sessionDate = new Date(`${date}T${time}`);
    return sessionDate.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'confirmed':
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
      case 'no-show':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "New password and confirmation don't match.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }

      toast({
        title: "Password Changed",
        description: "Your password has been successfully updated.",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  // Scheduling handlers (simplified for slot-based)
  const handleDayToggle = (day: string) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled
      }
    }));
  };

  const handleSaveWeeklySchedule = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('auth_token');

      // Save slots directly - no transformation
      const availabilityData: Record<string, string[]> = {};
      Object.entries(weeklySchedule).forEach(([day, config]) => {
        if (config.enabled && Array.isArray(config.slots) && config.slots.length > 0) {
          availabilityData[day.toLowerCase()] = config.slots;
        }
      });

      console.log('✅ Saving weekly schedule (slot-based):', availabilityData);

      const response = await fetch('/api/therapists/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          availability: JSON.stringify(availabilityData)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Failed to save schedule:', error);
        throw new Error(error.message || 'Failed to save schedule');
      }

      const result = await response.json();
      console.log('✅ Schedule saved successfully:', result);

      toast({
        title: "Success",
        description: "Weekly schedule saved successfully.",
      });
    } catch (error) {
      console.error('❌ Save schedule error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save schedule",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddBlockedDate = () => {
    if (!newBlockDate) {
      toast({
        title: "Date Required",
        description: "Please select a date to block",
        variant: "destructive"
      });
      return;
    }

    setBlockedDates(prev => [...prev, { date: newBlockDate, reason: newBlockReason }]);
    setNewBlockDate("");
    setNewBlockReason("");

    toast({
      title: "Date Blocked",
      description: "The selected date has been blocked from booking",
    });
  };

  const handleRemoveBlockedDate = (date: string) => {
    setBlockedDates(prev => prev.filter(d => d.date !== date));
    toast({
      title: "Block Removed",
      description: "The date is now available for booking",
    });
  };

  if (!isAuthenticated || user?.role !== 'therapist') {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-2">Therapist Access Required</h2>
            <p className="text-gray-600">Redirecting to therapist login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Auto-expand profile accordion when on a profile sub-view
  useEffect(() => {
    if (activeView.startsWith('profile-')) {
      setIsProfileOpen(true);
    }
  }, [activeView]);

  const navigationItems = [
    { id: 'sessions' as TherapistView, label: 'My Sessions', icon: Video },
    { id: 'clients' as TherapistView, label: 'Clients', icon: Users },
    { id: 'schedule' as TherapistView, label: 'Schedule', icon: Calendar },
    { id: 'security' as TherapistView, label: 'Security', icon: Lock },
  ];

  const profileSubItems = [
    { id: 'profile-account' as TherapistView, label: 'Account Info', icon: UserCog },
    { id: 'profile-professional' as TherapistView, label: 'Professional', icon: Award },
    { id: 'profile-location' as TherapistView, label: 'Location', icon: MapPin },
    { id: 'profile-certifications' as TherapistView, label: 'Certifications', icon: FileText },
    { id: 'profile-pricing' as TherapistView, label: 'Pricing', icon: Award },
  ];

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full bg-gray-50 flex-col">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setLocation('/')}>
              <div className="w-10 h-10 flex items-center justify-center">
                <img src="/owl-logo.png" alt="HealthyOwl Logo" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Healthy Owl</h1>
                <p className="text-xs text-gray-500">Therapist Portal</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">Therapist</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 relative">
          {/* Left Sidebar */}
          <Sidebar collapsible="icon" className="border-r border-gray-200 absolute left-0 top-0 bottom-0 h-full">
            <SidebarHeader className="border-b border-gray-200 px-3 py-3 gap-0">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center group-data-[collapsible=icon]:mx-auto">
                <User className="w-5 h-5 text-white" />
              </div>
            </SidebarHeader>

            <SidebarContent>
              <SidebarMenu>
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;

                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => setActiveView(item.id)}
                        isActive={isActive}
                        tooltip={item.label}
                        className={cn(
                          "w-full",
                          isActive && "bg-teal-50 text-teal-700"
                        )}
                      >
                        <Icon className={cn("w-5 h-5", isActive ? "text-teal-600" : "text-gray-400")} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}

                {/* Profile Accordion */}
                <Collapsible
                  open={isProfileOpen}
                  onOpenChange={setIsProfileOpen}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip="Profile"
                        className={cn(
                          "w-full",
                          (activeView.startsWith('profile-')) && "bg-teal-50 text-teal-700"
                        )}
                      >
                        <User className={cn("w-5 h-5", (activeView.startsWith('profile-')) ? "text-teal-600" : "text-gray-400")} />
                        <span>Profile</span>
                        <ChevronDown className={cn(
                          "ml-auto h-4 w-4 transition-transform duration-200",
                          isProfileOpen && "rotate-180"
                        )} />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {profileSubItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeView === subItem.id;

                          return (
                            <SidebarMenuSubItem key={subItem.id}>
                              <SidebarMenuSubButton
                                onClick={() => setActiveView(subItem.id)}
                                isActive={isSubActive}
                                className={cn(
                                  isSubActive && "bg-teal-50 text-teal-700"
                                )}
                              >
                                <SubIcon className="w-4 h-4" />
                                <span>{subItem.label}</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>

          {/* Main Content */}
          <SidebarInset>
            <div className="container mx-auto px-6 py-8 max-w-7xl">
              {/* Sessions View */}
              {activeView === 'sessions' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">My Sessions</h1>
                    <p className="text-gray-600 mt-1">Manage your therapy sessions</p>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-teal-600">
                          {getSessionStats().todaySessions}
                        </div>
                        <div className="text-sm text-gray-600">Today's Sessions</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {getSessionStats().weekSessions}
                        </div>
                        <div className="text-sm text-gray-600">This Week</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {getSessionStats().uniqueClients}
                        </div>
                        <div className="text-sm text-gray-600">Total Clients</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Filter Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card 
                      className={`cursor-pointer transition-all ${sessionFilter === 'all' ? 'ring-2 ring-teal-600 bg-teal-50' : 'hover:shadow-md'}`}
                      onClick={() => setSessionFilter('all')}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900">{getFilterCounts().all}</div>
                        <div className="text-sm text-gray-600">All Sessions</div>
                      </CardContent>
                    </Card>
                    <Card 
                      className={`cursor-pointer transition-all ${sessionFilter === 'upcoming' ? 'ring-2 ring-blue-600 bg-blue-50' : 'hover:shadow-md'}`}
                      onClick={() => setSessionFilter('upcoming')}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{getFilterCounts().upcoming}</div>
                        <div className="text-sm text-gray-600">Upcoming</div>
                      </CardContent>
                    </Card>
                    <Card 
                      className={`cursor-pointer transition-all ${sessionFilter === 'completed' ? 'ring-2 ring-green-600 bg-green-50' : 'hover:shadow-md'}`}
                      onClick={() => setSessionFilter('completed')}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{getFilterCounts().completed}</div>
                        <div className="text-sm text-gray-600">Completed</div>
                      </CardContent>
                    </Card>
                    <Card 
                      className={`cursor-pointer transition-all ${sessionFilter === 'cancelled' ? 'ring-2 ring-red-600 bg-red-50' : 'hover:shadow-md'}`}
                      onClick={() => setSessionFilter('cancelled')}
                    >
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-red-600">{getFilterCounts().cancelled}</div>
                        <div className="text-sm text-gray-600">Cancelled</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sessions List */}
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        {sessionFilter === 'all' && 'All Sessions'}
                        {sessionFilter === 'upcoming' && 'Upcoming Sessions'}
                        {sessionFilter === 'completed' && 'Past Sessions (Completed)'}
                        {sessionFilter === 'cancelled' && 'Cancelled Sessions'}
                      </CardTitle>
                      <CardDescription>
                        {getFilteredSessions().length} session(s) found
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {sessionsLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                          <p className="text-gray-500 mt-4">Loading sessions...</p>
                        </div>
                      ) : getFilteredSessions().length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-2">
                            No {sessionFilter !== 'all' ? sessionFilter : ''} sessions found
                          </p>
                          <p className="text-xs text-gray-400">
                            Sessions booked with you will appear here automatically.
                          </p>
                          {sessions.length === 0 && (
                            <p className="text-xs text-gray-400 mt-2">
                              Total sessions loaded: {sessions.length}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {getFilteredSessions()
                            .sort((a, b) => {
                              const dateA = new Date(`${a.date}T${a.time}`);
                              const dateB = new Date(`${b.date}T${b.time}`);
                              return dateB.getTime() - dateA.getTime(); // Most recent first
                            })
                            .map((session) => (
                              <div 
                                key={session.id} 
                                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <h3 className="font-semibold text-lg">
                                        Session #{session.sessionId || session.id}
                                      </h3>
                                      <Badge className={getStatusBadgeColor(session.status)}>
                                        {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                                      </Badge>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                      <p className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        {formatSessionDate(session.date, session.time)}
                                      </p>
                                      <p className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        {session.duration || '50 minutes'}
                                      </p>
                                      <p className="flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        Client ID: {session.clientUserId}
                                      </p>
                                      {session.notes && (
                                        <p className="flex items-start gap-2 mt-2">
                                          <span className="font-medium">Notes:</span>
                                          <span className="text-gray-700">{session.notes}</span>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {session.sessionType === 'human' ? 'Individual Session' : 
                                     session.sessionType === 'group' ? 'Group Session' : 'AI Session'}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Clients View */}
              {activeView === 'clients' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">My Clients</h1>
                    <p className="text-gray-600 mt-1">Manage your client list</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Client List</CardTitle>
                      <CardDescription>All your active clients</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-center py-8 text-gray-500">No clients yet</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Schedule View */}
              {activeView === 'schedule' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">My Schedule</h1>
                    <p className="text-gray-600 mt-1">Manage your availability and blocked dates</p>
                  </div>

                  {/* Weekly Availability Editor */}
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>Weekly Availability</CardTitle>
                      <CardDescription>Select available time slots (1-hour slots from 9 AM to 8 PM)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Quick-select buttons */}
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <Label className="w-full text-sm font-semibold mb-1">Quick Select:</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const selectedDay = Object.keys(weeklySchedule).find(d => weeklySchedule[d].enabled);
                            if (!selectedDay) {
                              toast({ title: "Enable a day first", description: "Check a day to apply quick-select", variant: "destructive" });
                              return;
                            }
                            Object.keys(weeklySchedule).forEach((day) => {
                              if (weeklySchedule[day].enabled) {
                                setWeeklySchedule(prev => ({
                                  ...prev,
                                  [day]: { 
                                    ...prev[day], 
                                    slots: ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM']
                                  }
                                }));
                              }
                            });
                          }}
                        >
                          Morning (9-12)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            Object.keys(weeklySchedule).forEach((day) => {
                              if (weeklySchedule[day].enabled) {
                                setWeeklySchedule(prev => ({
                                  ...prev,
                                  [day]: { 
                                    ...prev[day], 
                                    slots: ['1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM']
                                  }
                                }));
                              }
                            });
                          }}
                        >
                          Afternoon (1-5)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            Object.keys(weeklySchedule).forEach((day) => {
                              if (weeklySchedule[day].enabled) {
                                setWeeklySchedule(prev => ({
                                  ...prev,
                                  [day]: { 
                                    ...prev[day], 
                                    slots: ['6:00 PM', '7:00 PM', '8:00 PM']
                                  }
                                }));
                              }
                            });
                          }}
                        >
                          Evening (6-8)
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const monday = weeklySchedule.Monday;
                            if (!monday.enabled || !Array.isArray(monday.slots) || monday.slots.length === 0) {
                              toast({ title: "Set Monday first", description: "Configure Monday's slots to copy to other weekdays", variant: "destructive" });
                              return;
                            }
                            setWeeklySchedule(prev => ({
                              ...prev,
                              Tuesday: { enabled: true, slots: [...monday.slots] },
                              Wednesday: { enabled: true, slots: [...monday.slots] },
                              Thursday: { enabled: true, slots: [...monday.slots] },
                              Friday: { enabled: true, slots: [...monday.slots] }
                            }));
                            toast({ title: "Copied!", description: "Monday's schedule copied to Tue-Fri" });
                          }}
                        >
                          📋 Copy Mon → All Weekdays
                        </Button>
                      </div>

                      {/* Time slot grid for each day */}
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                        const availableSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'];
                        const selectedSlots = Array.isArray(weeklySchedule[day].slots) ? weeklySchedule[day].slots : [];

                        return (
                          <div key={day} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  id={`${day}-enabled`}
                                  checked={weeklySchedule[day].enabled}
                                  onChange={() => handleDayToggle(day)}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                                />
                                <Label htmlFor={`${day}-enabled`} className="text-base font-semibold">
                                  {day}
                                </Label>
                                {weeklySchedule[day].enabled && selectedSlots.length > 0 && (
                                  <Badge variant="secondary" className="ml-2">
                                    {selectedSlots.length} slot(s)
                                  </Badge>
                                )}
                              </div>
                              {weeklySchedule[day].enabled && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setWeeklySchedule(prev => ({
                                      ...prev,
                                      [day]: { ...prev[day], slots: [] }
                                    }));
                                  }}
                                >
                                  Clear All
                                </Button>
                              )}
                            </div>

                            {weeklySchedule[day].enabled && (
                              <div className="grid grid-cols-4 gap-2 ml-7">
                                {availableSlots.map((slot) => {
                                  const isSelected = selectedSlots.includes(slot);
                                  return (
                                    <label
                                      key={slot}
                                      className={`flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors ${
                                        isSelected 
                                          ? 'bg-teal-100 border-teal-600 text-teal-900' 
                                          : 'bg-white border-gray-200 hover:bg-gray-50'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          setWeeklySchedule(prev => ({
                                            ...prev,
                                            [day]: {
                                              ...prev[day],
                                              slots: e.target.checked
                                                ? [...selectedSlots, slot]
                                                : selectedSlots.filter(s => s !== slot)
                                            }
                                          }));
                                        }}
                                        className="sr-only"
                                      />
                                      <span className="text-sm font-medium">{slot}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {!weeklySchedule[day].enabled && (
                              <p className="text-sm text-gray-500 ml-7">
                                Enable this day to select time slots
                              </p>
                            )}
                          </div>
                        );
                      })}

                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          className="bg-teal-600 hover:bg-teal-700"
                          onClick={handleSaveWeeklySchedule}
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save Weekly Schedule"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Special Dates Manager */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Special Dates</CardTitle>
                      <CardDescription>Block specific dates for holidays, vacation, or time off</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Add blocked date form */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-semibold mb-3">Add Blocked Date</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="block-date">Date</Label>
                            <Input
                              id="block-date"
                              type="date"
                              value={newBlockDate}
                              onChange={(e) => setNewBlockDate(e.target.value)}
                              className="bg-white"
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="block-reason">Reason (Optional)</Label>
                            <Input
                              id="block-reason"
                              type="text"
                              value={newBlockReason}
                              onChange={(e) => setNewBlockReason(e.target.value)}
                              placeholder="e.g., Holiday, Vacation"
                              className="bg-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>&nbsp;</Label>
                            <Button 
                              className="w-full bg-teal-600 hover:bg-teal-700"
                              onClick={handleAddBlockedDate}
                            >
                              Add Block
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* List of blocked dates */}
                      <div className="space-y-2">
                        <h3 className="font-semibold">Blocked Dates</h3>
                        <div className="border rounded-lg divide-y">
                          {blockedDates.length > 0 ? (
                            blockedDates.map((block, index) => (
                              <div key={index} className="p-3 flex items-center justify-between hover:bg-gray-50">
                                <div>
                                  <p className="font-medium">
                                    {new Date(block.date).toLocaleDateString('en-US', { 
                                      weekday: 'long', 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    })}
                                  </p>
                                  {block.reason && (
                                    <p className="text-sm text-gray-500">{block.reason}</p>
                                  )}
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-600"
                                  onClick={() => handleRemoveBlockedDate(block.date)}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))
                          ) : (
                            <div className="p-8 text-center text-gray-500">
                              <p>No blocked dates yet</p>
                              <p className="text-sm">Add dates above to block them from booking</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Profile - Account Info Tab */}
              {activeView === 'profile-account' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Account Information</h1>
                    <p className="text-gray-600 mt-1">Manage your account details</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Account Details</CardTitle>
                      <CardDescription>Your basic account information</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        {/* Public Profile Section */}
                        <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                          <Label className="text-sm font-semibold text-teal-900 mb-2 block">Your Public Profile</Label>
                          <div className="flex items-center gap-2 mb-3">
                            <Input
                              type="text"
                              value={therapist?.slug 
                                ? `https://www.healthyowl.in/therapist-profile/${therapist.slug}`
                                : `https://www.healthyowl.in/therapist-profile/${user?.id}`
                              }
                              readOnly
                              className="bg-white text-sm"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const profileUrl = therapist?.slug 
                                  ? `https://www.healthyowl.in/therapist-profile/${therapist.slug}`
                                  : `https://www.healthyowl.in/therapist-profile/${user?.id}`;
                                navigator.clipboard.writeText(profileUrl);
                                toast({
                                  title: "Link Copied!",
                                  description: "Profile link copied to clipboard",
                                });
                              }}
                            >
                              Copy
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              className="bg-[#25D366] hover:bg-[#20BA5A] text-white flex items-center gap-2"
                              onClick={() => {
                                const profileUrl = therapist?.slug 
                                  ? `https://www.healthyowl.in/therapist-profile/${therapist.slug}`
                                  : `https://www.healthyowl.in/therapist-profile/${user?.id}`;
                                const message = encodeURIComponent(`I'm available for wellness sessions on HealthyOwl. Click to book: ${profileUrl}`);
                                window.open(`https://wa.me/?text=${message}`, '_blank');
                              }}
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                              </svg>
                              Share on WhatsApp
                            </Button>
                          </div>
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                          <Input
                            id="email"
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="bg-gray-50"
                          />
                          <p className="text-xs text-gray-500">Email cannot be changed</p>
                        </div>
                        <div className="grid gap-2" data-error={!!accountErrors.firstName}>
                          <Label htmlFor="firstName" className={accountErrors.firstName ? "text-red-600" : ""}>
                            First Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => {
                              setFirstName(e.target.value);
                              if (accountErrors.firstName) {
                                setAccountErrors({ ...accountErrors, firstName: undefined });
                              }
                            }}
                            placeholder="Enter first name"
                            className={accountErrors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}
                          />
                          {accountErrors.firstName && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {accountErrors.firstName}
                            </p>
                          )}
                        </div>
                        <div className="grid gap-2" data-error={!!accountErrors.lastName}>
                          <Label htmlFor="lastName" className={accountErrors.lastName ? "text-red-600" : ""}>
                            Last Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => {
                              setLastName(e.target.value);
                              if (accountErrors.lastName) {
                                setAccountErrors({ ...accountErrors, lastName: undefined });
                              }
                            }}
                            placeholder="Enter last name"
                            className={accountErrors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}
                          />
                          {accountErrors.lastName && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {accountErrors.lastName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          onClick={handleSaveAccountInfo}
                          disabled={isSaving}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Profile - Professional Tab */}
              {activeView === 'profile-professional' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Professional Credentials</h1>
                    <p className="text-gray-600 mt-1">Your professional information and credentials</p>
                    <p className="text-sm text-gray-500 mt-2">
                      <span className="text-red-500">*</span> Fields marked with asterisk are required
                    </p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Professional Details</CardTitle>
                      <CardDescription>Manage your professional credentials (minimum 1 selection required for each multi-select field)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="licenseNumber">License Number <span className="text-red-500">*</span></Label>
                          <Input
                            id="licenseNumber"
                            type="text"
                            value={licenseNumber}
                            disabled
                            className="bg-gray-50"
                          />
                          <p className="text-xs text-gray-500">License number cannot be changed. Contact admin for updates.</p>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="specialization">Specialization</Label>
                          <select
                            id="specialization"
                            value={specialization}
                            onChange={(e) => setSpecialization(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">Select specialization</option>
                            <option value="anxiety">Anxiety & Stress Management</option>
                            <option value="depression">Depression & Mood Disorders</option>
                            <option value="trauma">Trauma & PTSD</option>
                            <option value="relationship">Relationship & Family Therapy</option>
                            <option value="addiction">Addiction & Substance Abuse</option>
                            <option value="grief">Grief & Loss Counseling</option>
                            <option value="eating">Eating Disorders</option>
                            <option value="sleep">Sleep Disorders</option>
                            <option value="career">Career Counseling</option>
                            <option value="general">General Mental Health</option>
                          </select>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="experience">Years of Experience</Label>
                          <Input
                            id="experience"
                            type="number"
                            min="0"
                            value={experience}
                            onChange={(e) => setExperience(e.target.value)}
                            placeholder="Enter years of experience"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="title">Professional Title</Label>
                          <Input
                            id="title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Licensed Clinical Psychologist"
                          />
                        </div>

                        {/* Therapy Approaches - Mandatory */}
                        <div className="space-y-2" data-error={!!professionalErrors.approaches}>
                          <Label className={professionalErrors.approaches ? "text-red-600" : ""}>
                            Therapy Approaches <span className="text-red-500">*</span>
                          </Label>
                          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                            professionalErrors.approaches ? "border-2 border-red-500" : "border border-gray-200"
                          }`}>
                            {['CBT (Cognitive Behavioral Therapy)', 'DBT (Dialectical Behavior Therapy)', 'Psychodynamic Therapy', 'Mindfulness-Based Therapy', 'Solution-Focused Therapy', 'Humanistic Therapy', 'EMDR', 'ACT (Acceptance and Commitment Therapy)'].map((approach) => (
                              <label 
                                key={approach} 
                                className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                <input 
                                  type="checkbox" 
                                  checked={therapyApproaches.includes(approach)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setTherapyApproaches([...therapyApproaches, approach]);
                                    } else {
                                      setTherapyApproaches(therapyApproaches.filter(a => a !== approach));
                                    }
                                    if (professionalErrors.approaches) {
                                      setProfessionalErrors({ ...professionalErrors, approaches: undefined });
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" 
                                />
                                <span className="text-sm text-gray-700">{approach}</span>
                              </label>
                            ))}
                          </div>
                          {professionalErrors.approaches && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {professionalErrors.approaches}
                            </p>
                          )}
                        </div>

                        {/* Session Types - Mandatory */}
                        <div className="space-y-2" data-error={!!professionalErrors.sessionTypes}>
                          <Label className={professionalErrors.sessionTypes ? "text-red-600" : ""}>
                            Session Types <span className="text-red-500">*</span>
                          </Label>
                          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                            professionalErrors.sessionTypes ? "border-2 border-red-500" : "border border-gray-200"
                          }`}>
                            {['Individual Session', 'Couples Session', 'Family Session', 'Group Session', 'Online Sessions', 'In-Person Sessions'].map((sessionType) => (
                              <label 
                                key={sessionType} 
                                className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                <input 
                                  type="checkbox"
                                  checked={sessionTypes.includes(sessionType)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSessionTypes([...sessionTypes, sessionType]);
                                    } else {
                                      setSessionTypes(sessionTypes.filter(s => s !== sessionType));
                                    }
                                    if (professionalErrors.sessionTypes) {
                                      setProfessionalErrors({ ...professionalErrors, sessionTypes: undefined });
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" 
                                />
                                <span className="text-sm text-gray-700">{sessionType}</span>
                              </label>
                            ))}
                          </div>
                          {professionalErrors.sessionTypes && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {professionalErrors.sessionTypes}
                            </p>
                          )}
                        </div>

                        {/* Languages - Mandatory */}
                        <div className="space-y-2" data-error={!!professionalErrors.languages}>
                          <Label className={professionalErrors.languages ? "text-red-600" : ""}>
                            Languages <span className="text-red-500">*</span>
                          </Label>
                          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                            professionalErrors.languages ? "border-2 border-red-500" : "border border-gray-200"
                          }`}>
                            {['English', 'Spanish', 'French', 'German', 'Mandarin', 'Hindi', 'Arabic', 'Portuguese'].map((language) => (
                              <label 
                                key={language} 
                                className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                <input 
                                  type="checkbox"
                                  checked={languages.includes(language)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setLanguages([...languages, language]);
                                    } else {
                                      setLanguages(languages.filter(l => l !== language));
                                    }
                                    if (professionalErrors.languages) {
                                      setProfessionalErrors({ ...professionalErrors, languages: undefined });
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" 
                                />
                                <span className="text-sm text-gray-700">{language}</span>
                              </label>
                            ))}
                          </div>
                          {professionalErrors.languages && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {professionalErrors.languages}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          onClick={handleSaveProfessional}
                          disabled={isSaving}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Profile - Location Tab */}
              {activeView === 'profile-location' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Location & Profile</h1>
                    <p className="text-gray-600 mt-1">Your location and public profile information</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Profile Information</CardTitle>
                      <CardDescription>Information visible to clients</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        <div className="grid gap-2" data-error={!!locationErrors.bio}>
                          <Label htmlFor="bio" className={locationErrors.bio ? "text-red-600" : ""}>Bio</Label>
                          <textarea
                            id="bio"
                            value={bio}
                            onChange={(e) => {
                              setBio(e.target.value);
                              if (locationErrors.bio) {
                                setLocationErrors({ ...locationErrors, bio: undefined });
                              }
                            }}
                            className={`flex min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 resize-none ${
                              locationErrors.bio 
                                ? "border-red-500 focus-visible:ring-red-500" 
                                : "border-input focus-visible:ring-ring"
                            }`}
                            placeholder="Tell clients about yourself, your approach, and what makes you unique..."
                            maxLength={500}
                          />
                          <p className={`text-xs ${bio.length > 500 ? 'text-red-600' : 'text-gray-500'}`}>
                            {bio.length}/500 characters
                          </p>
                          {locationErrors.bio && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {locationErrors.bio}
                            </p>
                          )}
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="avatar">Profile Photo</Label>
                          <div className="flex items-center gap-4 p-4 border rounded-lg bg-gray-50">
                            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                              <User className="w-8 h-8 text-teal-600" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700">Coming Soon</p>
                              <p className="text-xs text-gray-500">Photo upload feature will be available soon</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="location">Location</Label>
                          <Input
                            id="location"
                            type="text"
                            value={location}
                            onChange={(e) => setLocationField(e.target.value)}
                            placeholder="e.g., New York, NY or Online"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="timezone">Timezone</Label>
                          <select
                            id="timezone"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="UTC">UTC (GMT+0:00)</option>
                            <option value="America/New_York">Eastern Time (GMT-5:00)</option>
                            <option value="America/Chicago">Central Time (GMT-6:00)</option>
                            <option value="America/Denver">Mountain Time (GMT-7:00)</option>
                            <option value="America/Los_Angeles">Pacific Time (GMT-8:00)</option>
                            <option value="Europe/London">London (GMT+0:00)</option>
                            <option value="Europe/Paris">Paris (GMT+1:00)</option>
                            <option value="Asia/Dubai">Dubai (GMT+4:00)</option>
                            <option value="Asia/Kolkata">India (GMT+5:30)</option>
                            <option value="Asia/Singapore">Singapore (GMT+8:00)</option>
                            <option value="Asia/Tokyo">Tokyo (GMT+9:00)</option>
                            <option value="Australia/Sydney">Sydney (GMT+11:00)</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end pt-4 border-t">
                        <Button 
                          onClick={handleSaveLocation}
                          disabled={isSaving}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Profile - Certifications Tab */}
              {activeView === 'profile-certifications' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Certifications & Extended Details</h1>
                    <p className="text-gray-600 mt-1">Your education, certifications, and therapeutic approaches</p>
                  </div>

                  <div className="space-y-6">
                    {/* Education */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Education</CardTitle>
                        <CardDescription>Your academic qualifications</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Education Entries</Label>
                          <div className="space-y-2">
                            {education.map((edu, index) => (
                              <div key={index} className="flex gap-2">
                                <Input 
                                  value={edu}
                                  onChange={(e) => {
                                    const newEducation = [...education];
                                    newEducation[index] = e.target.value;
                                    setEducation(newEducation);
                                  }}
                                  placeholder="e.g., PhD in Clinical Psychology, Harvard University" 
                                />
                                {education.length > 1 && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setEducation(education.filter((_, i) => i !== index))}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => setEducation([...education, ""])}
                            >
                              + Add Another Education Entry
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Certifications */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Certifications</CardTitle>
                        <CardDescription>Your professional certifications</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Certification Entries</Label>
                          <div className="space-y-2">
                            {certifications.map((cert, index) => (
                              <div key={index} className="flex gap-2">
                                <Input 
                                  value={cert}
                                  onChange={(e) => {
                                    const newCerts = [...certifications];
                                    newCerts[index] = e.target.value;
                                    setCertifications(newCerts);
                                  }}
                                  placeholder="e.g., Board Certified in Cognitive Behavioral Therapy" 
                                />
                                {certifications.length > 1 && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setCertifications(certifications.filter((_, i) => i !== index))}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full"
                              onClick={() => setCertifications([...certifications, ""])}
                            >
                              + Add Another Certification
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Insurance */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Insurance Accepted</CardTitle>
                        <CardDescription>Insurance providers you accept</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {['Blue Cross Blue Shield', 'Aetna', 'UnitedHealthcare', 'Cigna', 'Humana', 'Medicare', 'Medicaid', 'Out of Pocket'].map((ins) => (
                            <label 
                              key={ins} 
                              className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                            >
                              <input 
                                type="checkbox"
                                checked={insurance.includes(ins)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setInsurance([...insurance, ins]);
                                  } else {
                                    setInsurance(insurance.filter(i => i !== ins));
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500" 
                              />
                              <span className="text-sm text-gray-700">{ins}</span>
                            </label>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={handleSaveCertifications}
                        disabled={isSaving}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {isSaving ? "Saving..." : "Save All Changes"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Profile - Pricing Tab */}
              {activeView === 'profile-pricing' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Pricing & Session Details</h1>
                    <p className="text-gray-600 mt-1">Configure your session pricing and payment options</p>
                  </div>

                  <div className="space-y-6">
                    {/* Session Pricing */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Session Pricing</CardTitle>
                        <CardDescription>Set your rates for different session types</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Individual Sessions */}
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Individual Session</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="individual-price">Price (₹)</Label>
                              <Input
                                id="individual-price"
                                type="number"
                                min="0"
                                value={pricing.individual.price}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  individual: { ...pricing.individual, price: parseInt(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 2000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="individual-duration">Duration (minutes)</Label>
                              <Input
                                id="individual-duration"
                                type="number"
                                min="15"
                                max="120"
                                step="15"
                                value={pricing.individual.duration}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  individual: { ...pricing.individual, duration: parseInt(e.target.value) || 50 }
                                })}
                                placeholder="e.g., 50"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Couples Sessions */}
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Couples Session</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="couples-price">Price (₹)</Label>
                              <Input
                                id="couples-price"
                                type="number"
                                min="0"
                                value={pricing.couples.price}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  couples: { ...pricing.couples, price: parseInt(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 3000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="couples-duration">Duration (minutes)</Label>
                              <Input
                                id="couples-duration"
                                type="number"
                                min="15"
                                max="120"
                                step="15"
                                value={pricing.couples.duration}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  couples: { ...pricing.couples, duration: parseInt(e.target.value) || 60 }
                                })}
                                placeholder="e.g., 60"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Family Sessions */}
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Family Session</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="family-price">Price (₹)</Label>
                              <Input
                                id="family-price"
                                type="number"
                                min="0"
                                value={pricing.family.price}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  family: { ...pricing.family, price: parseInt(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 3500"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="family-duration">Duration (minutes)</Label>
                              <Input
                                id="family-duration"
                                type="number"
                                min="15"
                                max="120"
                                step="15"
                                value={pricing.family.duration}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  family: { ...pricing.family, duration: parseInt(e.target.value) || 60 }
                                })}
                                placeholder="e.g., 60"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Group Sessions */}
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Group Session</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="group-price">Price (₹)</Label>
                              <Input
                                id="group-price"
                                type="number"
                                min="0"
                                value={pricing.group.price}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  group: { ...pricing.group, price: parseInt(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 1000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="group-duration">Duration (minutes)</Label>
                              <Input
                                id="group-duration"
                                type="number"
                                min="15"
                                max="120"
                                step="15"
                                value={pricing.group.duration}
                                onChange={(e) => setPricing({
                                  ...pricing,
                                  group: { ...pricing.group, duration: parseInt(e.target.value) || 60 }
                                })}
                                placeholder="e.g., 60"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Payment Options */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Payment Options</CardTitle>
                        <CardDescription>Additional payment and consultation options</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-4">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={acceptsInsurance}
                              onChange={(e) => setAcceptsInsurance(e.target.checked)}
                              className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Accept Insurance</span>
                              <p className="text-sm text-gray-500">I accept insurance payments for sessions</p>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={slidingScale}
                              onChange={(e) => setSlidingScale(e.target.checked)}
                              className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Sliding Scale Available</span>
                              <p className="text-sm text-gray-500">I offer reduced rates based on income</p>
                            </div>
                          </label>

                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={freeConsultation}
                              onChange={(e) => setFreeConsultation(e.target.checked)}
                              className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            />
                            <div>
                              <span className="font-medium text-gray-900">Free Initial Consultation</span>
                              <p className="text-sm text-gray-500">Offer a free 15-minute consultation</p>
                            </div>
                          </label>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={handleSavePricing}
                        disabled={isSaving}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {isSaving ? "Saving..." : "Save Pricing"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security View */}
              {activeView === 'security' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Security Settings</h1>
                    <p className="text-gray-600 mt-1">Manage your account security</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Change Password</CardTitle>
                      <CardDescription>Update your account password</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="current-password">Current Password</Label>
                          <Input
                            id="current-password"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="new-password">New Password</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <p className="text-xs text-gray-500">Must be at least 8 characters long</p>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="confirm-password">Confirm New Password</Label>
                          <Input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </div>
                      <Button onClick={handlePasswordChange} className="bg-teal-600 hover:bg-teal-700">
                        Update Password
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </SidebarInset>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-4 mt-auto">
          <div className="container mx-auto px-6 text-center">
            <p className="text-sm text-slate-400">
              © 2025 The Owl Co. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </SidebarProvider>
  );
}